import {clsx} from 'clsx'
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CodeIcon,
  FileIcon,
  FoldIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {IconButton, Link, SegmentedControl} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import type {PatchStatus} from '@github-ui/diff-file-helpers'
import {fileModeChanged} from '@github-ui/diff-file-helpers'
import {DiffStats} from '@github-ui/diffs/DiffStats'
import {useRef, type ComponentProps, type MouseEvent, type ReactNode, type RefObject} from 'react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import styles from './DiffFileHeader.module.css'
import {DiffFeature} from '@github-ui/diffs/types'
import {useFileNameTooltip} from './use-file-name-tooltip'
import {reverseTruncate} from './reverse-truncate'

const defaultHiddenFeatures = new Set<DiffFeature>()

/**
 * Renders the file name for a renamed file.
 * Includes both the old and new file names with an arrow icon in between.
 * Renamed file names will not be truncated via CSS to ensure the arrow is always visible.
 */
function RenamedFileName({
  oldPath,
  newPath,
  oldPathTruncated,
  newPathTruncated,
}: {
  oldPath?: string | null
  newPath?: string | null
  oldPathTruncated?: string
  newPathTruncated?: string
}) {
  return (
    <code>
      <span aria-hidden="true">
        &lrm;{oldPathTruncated}&lrm; <ArrowRightIcon /> &lrm;{newPathTruncated}&lrm;
      </span>
      <span className="sr-only">
        {oldPath} renamed to {newPath}{' '}
      </span>
    </code>
  )
}

/**
 * Renders the file name for a single file.
 * Wraps the file name in a `<code>` element with left-to-right marks to truncate from the left.
 */
function FileName({path}: {path?: string | null}) {
  return <code>&lrm;{path}&lrm;</code>
}

/**
 * Returns the heading text, which also serves as the accessible name for the `region`.
 * Ensure this logic stays consistent with the table's aria-label in `DiffLines`
 * to maintain accessibility alignment.
 * @returns An object containing the full display text and truncation information
 */
function displayNameText({
  newPath,
  oldPath,
  status,
}: {
  newPath?: string | null
  oldPath?: string | null
  status: PatchStatus
}): {text: string; truncated: boolean; oldPathTruncated?: string; newPathTruncated?: string} {
  if (status === 'RENAMED') {
    // Truncate the old and new paths separately to ensure both ends are visible
    // Mimics the Rails implementation
    const oldPathTruncatedResult = reverseTruncate(oldPath)
    const newPathTruncatedResult = reverseTruncate(newPath)
    const truncated = oldPathTruncatedResult.truncated || newPathTruncatedResult.truncated
    return {
      text: `${oldPath} renamed to ${newPath}`,
      truncated,
      oldPathTruncated: oldPathTruncatedResult.text,
      newPathTruncated: newPathTruncatedResult.text,
    }
  } else if (status === 'DELETED' || status === 'REMOVED') {
    return {text: oldPath ?? '', truncated: false}
  } else {
    return {text: newPath ?? '', truncated: false}
  }
}

function HeadingFileName({
  headingId,
  fileLinkHref,
  onHeaderClick,
  fileLinkRef,
  newPath,
  oldPath,
  patchStatus,
}: {
  headingId?: string
  fileLinkHref?: string
  onHeaderClick?: (e: MouseEvent<HTMLElement>) => void
  fileLinkRef?: RefObject<HTMLAnchorElement | null>
  newPath?: string | null
  oldPath?: string | null
  patchStatus: PatchStatus
}) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const showFileNameTooltip = useFileNameTooltip(headingRef)

  const displayPath = displayNameText({newPath, oldPath, status: patchStatus})
  const fileName =
    patchStatus === 'RENAMED' ? (
      <RenamedFileName
        oldPath={oldPath}
        newPath={newPath}
        oldPathTruncated={displayPath.oldPathTruncated}
        newPathTruncated={displayPath.newPathTruncated}
      />
    ) : (
      <FileName path={displayPath.text} />
    )

  return (
    <h3
      id={headingId}
      className={clsx(styles['file-name'], patchStatus !== 'RENAMED' && styles['file-name-truncate'])}
      ref={headingRef}
    >
      {showFileNameTooltip || displayPath.truncated ? (
        <Tooltip text={displayPath.text} direction="s">
          <Link className="Link--primary" href={fileLinkHref} onClick={onHeaderClick} ref={fileLinkRef}>
            {fileName}
          </Link>
        </Tooltip>
      ) : (
        <Link className="Link--primary" href={fileLinkHref} onClick={onHeaderClick} ref={fileLinkRef}>
          {fileName}
        </Link>
      )}
    </h3>
  )
}

export interface DiffFileHeaderProps {
  /**
   * Optional additional content to render to the left of the file name
   */
  additionalLeftSideContent?: ReactNode
  /**
   * Indicates whether the lines in the file have all been expanded
   */
  areLinesExpanded?: boolean
  /**
   * Indicates whether the "expand all" button should be shown
   */
  canExpandOrCollapseLines?: boolean
  /**
   * CSS class that will be applied to the root header element
   */
  className?: string
  /**
   * Overrides for the diff stats component
   */
  diffStatsProps?: ComponentProps<typeof DiffStats>
  /**
   * A set of features to be hidden
   */
  hiddenFeatures?: Set<DiffFeature>
  /**
   * Indicates if the file is a binary file that may not be displayed
   */
  isBinary?: boolean
  /**
   * Indicates whether the file is in a collapsed state
   */
  isCollapsed?: boolean
  /**
   * Indicates if the file is a submodule diff
   */
  isSubmodule?: boolean
  /**
   * Indicates whether the diff can be displayed in rich mode
   */
  canToggleRichDiff?: boolean
  /**
   * Heading id
   */
  headingId?: string
  /**
   * Count of the number of lines added to the file
   */
  linesAdded: number
  /**
   * Count of the number of lines changed in the file
   */
  linesChanged: number
  /**
   * Count of the number of lines deleted from the file
   */
  linesDeleted: number
  /**
   * The href to use for the file name link
   */
  fileLinkHref?: string
  /**
   * A ref to use for the file name link
   */
  fileLinkRef?: RefObject<HTMLAnchorElement | null>
  /**
   * The new git mode of the file
   */
  newMode?: number
  /**
   * The new path of the file
   */
  newPath?: string | null
  /**
   * The old git mode of the file
   */
  oldMode?: number
  /**
   * The old path of the file
   */
  oldPath?: string | null
  /**
   * Callback invoked when the user clicks the button to copy the file path
   */
  onCopyPath?: () => void
  /**
   * Callback invoked when the user clicks the header
   */
  onHeaderClick?: (e: MouseEvent<HTMLElement>) => void
  /**
   * Callback invoked when the user clicks the button to toggle the expanded state of all lines
   */
  onToggleExpandAllLines?: () => void
  /**
   * Callback invoked when the user clicks the button to toggle the collapsed state of the file
   */
  onToggleFileCollapsed: (event: React.MouseEvent) => void
  /**
   * Callback invloked when the user clicks the button to toggle the display of the diff between rich and source
   */
  onToggleDiffDisplay?: (rich: boolean) => void
  /**
   * The status of the file
   */
  patchStatus: PatchStatus
  /**
   * The path of the file
   */
  path: string
  /**
   * Optional additional content to render after the file name. This content will be right-aligned.
   */
  rightSideContent?: ReactNode
  /**
   * The size of the diff, in human readable form with units, to show when there is a binary file
   */
  size?: string
  /**
   * Indicates whether the diff should be shown as a rich diff
   */
  showRichDiff?: boolean
  /**
   * Indicates whether the diff should be shown in a single file mode
   */
  isInSingleFileMode?: boolean
}

/**
 * Renders the header of a diff file.
 */
export function DiffFileHeader({
  additionalLeftSideContent,
  areLinesExpanded,
  canExpandOrCollapseLines,
  className,
  diffStatsProps,
  hiddenFeatures = defaultHiddenFeatures,
  isBinary,
  isSubmodule,
  isCollapsed,
  canToggleRichDiff,
  headingId,
  linesAdded,
  linesChanged,
  linesDeleted,
  fileLinkHref,
  fileLinkRef,
  newMode,
  newPath,
  oldMode,
  oldPath,
  onCopyPath,
  onHeaderClick,
  onToggleDiffDisplay,
  onToggleExpandAllLines,
  onToggleFileCollapsed,
  patchStatus,
  path,
  rightSideContent,
  size,
  showRichDiff,
  isInSingleFileMode,
}: DiffFileHeaderProps) {
  const isNegative = size && size[0] === '-'

  return (
    <div
      className={clsx(
        styles['diff-file-header'],
        isCollapsed ? styles['collapsed'] : '',
        styles['container-flex-wrap'],
        className,
      )}
    >
      {!isInSingleFileMode && (
        <div className="d-flex flex-shrink-0 flex-order-1" style={{width: '28px', justifyContent: 'center'}}>
          <IconButton
            onClick={onToggleFileCollapsed}
            aria-label={isCollapsed ? `Expand file` : `Collapse file`}
            icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
            size="small"
            variant="invisible"
          />
        </div>
      )}

      {additionalLeftSideContent}
      <div
        className={clsx(
          'd-flex px-1 flex-items-center overflow-hidden',
          styles['container-flex-order-reverse'],
          styles['file-path-section'],
          patchStatus !== 'RENAMED' && 'flex-1',
        )}
      >
        <HeadingFileName
          headingId={headingId}
          fileLinkHref={fileLinkHref}
          onHeaderClick={onHeaderClick}
          fileLinkRef={fileLinkRef}
          newPath={newPath}
          oldPath={oldPath}
          patchStatus={patchStatus}
        />
        <CopyToClipboardButton
          className={clsx('ml-2 flex-shrink-0', styles['hide-on-mobile'])}
          textToCopy={newPath ?? oldPath ?? ''}
          ariaLabel="Copy file name to clipboard"
          tooltipProps={{direction: 's'}}
          onCopy={onCopyPath}
          size="small"
        />
        {fileModeChanged(patchStatus, oldMode, newMode) && (
          <div className="p-2">
            <code>{oldMode}</code>
            <ArrowRightIcon className="mx-1" />
            <code>{newMode}</code>
          </div>
        )}
        {!hiddenFeatures.has(DiffFeature.expandLines) && canExpandOrCollapseLines && onToggleExpandAllLines && (
          <Tooltip
            text={areLinesExpanded ? `Collapse non-diff lines: ${path}` : `Expand all lines: ${path}`}
            direction="s"
          >
            <IconButton
              onClick={onToggleExpandAllLines}
              aria-label={areLinesExpanded ? `Collapse non-diff lines: ${path}` : `Expand all lines: ${path}`}
              icon={areLinesExpanded ? FoldIcon : UnfoldIcon}
              size="small"
              variant="invisible"
              data-file-path={path}
              className={clsx(
                'flex-shrink-0',
                !areLinesExpanded && 'js-expand-all-difflines-button',
                styles['hide-on-mobile'],
              )}
            />
          </Tooltip>
        )}
      </div>
      <div
        className={clsx(
          'd-flex flex-row flex-justify-end flex-items-center gap-2 flex-1',
          styles['container-flex-order'],
        )}
      >
        {!hiddenFeatures.has(DiffFeature.diffStats) && (
          <div className="d-flex flex-justify-end flex-items-center">
            {!isSubmodule && (
              <div className={styles['hide-on-mobile']}>
                <DiffStats
                  linesAdded={linesAdded}
                  linesDeleted={linesDeleted}
                  linesChanged={linesChanged}
                  {...diffStatsProps}
                />
              </div>
            )}
          </div>
        )}
        {isBinary && (
          <div className="d-flex flex-items-center">
            <code
              className={clsx('px-2', isNegative && 'fgColor-danger', !isNegative && 'fgColor-success')}
            >{`${size}`}</code>
          </div>
        )}
        {canToggleRichDiff && (
          <SegmentedControl
            aria-label="File view"
            size="small"
            className={styles['hide-on-small']}
            onChange={onToggleDiffDisplay ? index => onToggleDiffDisplay(index === 1) : undefined}
          >
            <SegmentedControl.IconButton
              aria-label="Display the source diff"
              selected={!showRichDiff}
              icon={CodeIcon}
            />
            <SegmentedControl.IconButton aria-label="Display the rich diff" selected={showRichDiff} icon={FileIcon} />
          </SegmentedControl>
        )}
        {rightSideContent}
      </div>
    </div>
  )
}

try{ RenamedFileName.displayName ||= 'RenamedFileName' } catch {}
try{ FileName.displayName ||= 'FileName' } catch {}
try{ HeadingFileName.displayName ||= 'HeadingFileName' } catch {}
try{ DiffFileHeader.displayName ||= 'DiffFileHeader' } catch {}